import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { Payment } from 'src/app/models/payment';
import { Utility } from 'src/app/helpers/utility-helper';
import { CardValidatorHelper } from 'src/app/helpers/card-validator-helper';
import { GiftCardService } from 'src/app/services/gift-card/gift-card.service';
import { Subject, takeUntil } from 'rxjs';
import { translate } from '@jsverse/transloco';

@Component({
  selector: 'app-gift-card-form',
  templateUrl: './gift-card-form.component.html',
  styleUrls: ['./gift-card-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GiftCardFormComponent implements OnInit, OnDestroy {
  @Input() amount: string | undefined = '0';
  @Output() submitForm: EventEmitter<any> = new EventEmitter();
  @Output() buttonClick: EventEmitter<any> = new EventEmitter();

  @ViewChild('indexCardNumber') indexCardNumber!: ElementRef;
  @ViewChild('indexCardPin') indexCardPin!: ElementRef;
  @ViewChild('indexRequestedAmount') indexRequestedAmount!: ElementRef;

  public paymentData!: Payment;
  private unsubscribe$ = new Subject<any>();
  balanceDueFixedPointFormat = '0';
  balanceDueCurrencyFormat = '0';
  decimalSeparator = Utility.DefaultDecimalSeparator;

  constructor(
    private renderer: Renderer2,
    private giftCardService: GiftCardService
  ) { }

  ngOnInit(): void {
    this.initializePaymentData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next({});
    this.unsubscribe$.complete();
  }

  private initializePaymentData() {
    this.paymentData = this.giftCardService.getInitialPayment(this.amount || '0');
    this.balanceDueFixedPointFormat = this.giftCardService.getBalanceDue();
    this.balanceDueCurrencyFormat = this.giftCardService.getFormattedBalanceDue();
    this.decimalSeparator = this.giftCardService.getCurrentCultureDecimalSeparator();
  }

  async handlePayClick(): Promise<void> {

    this.hideAllCardFormErrors();

    this.buttonClick.emit(true);

    await Utility.Timeout(1);

    if (this.validateFileds()) {
      this.excecuteTransaction();
    }
    else {
      this.buttonClick.emit(false);
    }
  }

  private excecuteTransaction(): void {
    const payment = {...this.paymentData, RedactedInfo: this.paymentData.PaymentReference.slice(-6) };

    this.giftCardService
      .excecuteSaleTransaction(payment)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response: any) => {
        this.submitForm.emit(response);
      });
  }

  private validateFileds(): boolean {

    let isValid = true;

    // amount validation
    if (!Utility.stringIsNotNullOrEmpty(this.paymentData?.RequestedAmount)) {
      this.setAmountValidationError();

      isValid = false;
    }
    else if (!CardValidatorHelper.IsPostiveDecimalAmount(this.paymentData?.RequestedAmount, this.decimalSeparator)) {
      this.setAmountFormatValidationError();

      isValid = false;
    }
    else if (!CardValidatorHelper.IsValidRequestedAmount(this.paymentData?.RequestedAmount, this.balanceDueFixedPointFormat, this.decimalSeparator)) {
      this.setAmountValidationError();

      isValid = false;
    }

    if (!Utility.stringIsNotNullOrEmpty(this.paymentData.PaymentReference)) {
      this.setCardNumberValidationError();

      isValid = false;
    }

    if (!Utility.stringIsNotNullOrEmpty(this.paymentData.CardPIN)) {
      this.setCardPinValidationError();

      isValid = false;
    }

    return isValid;
  }

  private hideAllCardFormErrors() {
    this.renderer.setProperty(this.indexCardNumber.nativeElement, 'error', '');
    this.renderer.setProperty(this.indexCardPin.nativeElement, 'error', '');
    this.renderer.setProperty(this.indexRequestedAmount.nativeElement,'error','');
  }

  private setAmountValidationError = () =>
    (() => {
      const error = translate('AmountRequired', { value1: 0, value2: this.balanceDueFixedPointFormat });

      this.renderer.setProperty(
        this.indexRequestedAmount.nativeElement,
        'error',
        error
      );
    })();

  private setCardNumberValidationError = () =>
    (() =>
      this.renderer.setProperty(
        this.indexCardNumber.nativeElement,
        'error',
        translate('CardNumberRequired')
      ))();

  private setAmountFormatValidationError = () =>
    (() => {
      const error = translate('AmountFormatError', { value1: this.decimalSeparator });

      this.renderer.setProperty(
        this.indexRequestedAmount.nativeElement,
        'error',
        error
      );
    })();

  private setCardPinValidationError = () =>
    (() =>
      this.renderer.setProperty(
        this.indexCardPin.nativeElement,
        'error',
        translate('CardPinRequired')
      ))();
}
